// Taps a MediaStream's mono PCM through an AudioWorklet. The worklet buffers
// render quanta (128 frames) into ~85ms chunks so the port isn't flooded with
// hundreds of messages a second. Capture runs at the AudioContext's native
// sample rate — the consumer tells the STT service that rate instead of
// resampling here.
const WORKLET_NAME = 'pcm-capture'
const CHUNK_FRAMES = 4096

const WORKLET_SOURCE = `
class PcmCaptureProcessor extends AudioWorkletProcessor {
  constructor() {
    super()
    this.buffer = new Float32Array(${CHUNK_FRAMES})
    this.length = 0
  }
  process(inputs) {
    const channel = inputs[0][0]
    if (!channel) return true
    let offset = 0
    while (offset < channel.length) {
      const take = Math.min(
        channel.length - offset,
        this.buffer.length - this.length,
      )
      this.buffer.set(channel.subarray(offset, offset + take), this.length)
      this.length += take
      offset += take
      if (this.length === this.buffer.length) {
        this.port.postMessage(this.buffer.slice(0))
        this.length = 0
      }
    }
    return true
  }
}
registerProcessor('${WORKLET_NAME}', PcmCaptureProcessor)
`

export interface PcmCapture {
  /** The context's actual rate — pass to the STT service as-is. */
  sampleRate: number
  stop: () => void
}

// Capture at 16 kHz when the browser can resample (Chromium can): speech STT
// gains nothing above it and the streamed payload shrinks 3×. The fallback
// keeps the device's native rate — the STT endpoint accepts standard rates.
function createCaptureContext(): AudioContext {
  try {
    return new AudioContext({ sampleRate: 16000 })
  } catch {
    return new AudioContext()
  }
}

export async function startPcmCapture(
  stream: MediaStream,
  onChunk: (samples: Float32Array) => void,
): Promise<PcmCapture> {
  const context = createCaptureContext()
  // A context created outside a user gesture starts suspended; a call answer
  // click is recent enough that resume() succeeds.
  void context.resume().catch(() => undefined)
  const moduleUrl = URL.createObjectURL(
    new Blob([WORKLET_SOURCE], { type: 'application/javascript' }),
  )
  try {
    await context.audioWorklet.addModule(moduleUrl)
  } finally {
    URL.revokeObjectURL(moduleUrl)
  }
  const source = context.createMediaStreamSource(stream)
  const worklet = new AudioWorkletNode(context, WORKLET_NAME, {
    numberOfInputs: 1,
    numberOfOutputs: 0,
  })
  worklet.port.onmessage = (event: MessageEvent<Float32Array>) => {
    onChunk(event.data)
  }
  source.connect(worklet)
  return {
    sampleRate: context.sampleRate,
    stop: () => {
      worklet.port.onmessage = null
      source.disconnect()
      worklet.disconnect()
      void context.close().catch(() => undefined)
    },
  }
}
